import { CurrencyAmount, Token } from '@uniswap/sdk-core'
import IErc20ABI from 'abis/erc20.json'
import MultipoolAbi from 'abis/Multipool.json'
import MultipoolDispatcherAbi from 'abis/MultipoolDispatcher.json'
import { Erc20Interface } from 'abis/types/Erc20'
import { MultipoolInterface } from 'abis/types/Multipool'
import { MultipoolDispatcher, MultipoolDispatcherInterface } from 'abis/types/MultipoolDispatcher'
import { SupportedChainId } from 'constants/chains'
import { DEFAULT_ERC20_DECIMALS } from 'constants/tokens/constants'
import { BigNumber } from 'ethers'
import { Interface } from 'ethers/lib/utils'
import { Multipool } from 'lib/multipool'
import { MultipoolPosition } from 'lib/multipool/position'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { UniswapInterfaceMulticall } from 'types/v3'
import { currencyKey } from 'utils/currencyKey'

import { useGetCachedTokens } from '../Pools/cache'
import { Call, DEFAULT_GAS_LIMIT } from '../Pools/getTokensAsync'
import { useInterfaceMulticallContracts } from '../Pools/hooks'
import { PositionInfo, useCachedPositions } from './cache'
import { useMultipoolDispatcherContracts, useStrategyPriceMap } from './hooks'

const DEFAULT_CHAINS = [SupportedChainId.ARBITRUM]

type UseMultiChainPositionsData = { positions: PositionInfo[] | undefined; loading: boolean }

interface IPoolInfo {
  balance: BigNumber
  multipool: string
  multipoolToken: string
  token0: string
  token1: string
}

export default function useMultiChainStrategies(account: string, chains = DEFAULT_CHAINS): UseMultiChainPositionsData {
  const multipoolDispatchers = useMultipoolDispatcherContracts(chains)
  const multicalls = useInterfaceMulticallContracts(chains)
  const getTokens = useGetCachedTokens(chains)

  const [cachedPositions, setPositions] = useCachedPositions(account)
  const positions = cachedPositions?.result
  const positionsFetching = useRef(false)
  const positionsLoading = !cachedPositions?.result && positionsFetching.current

  const { priceMap, pricesLoading } = useStrategyPriceMap(positions)

  const fetchMultipools = useCallback(
    async (multicall: UniswapInterfaceMulticall, dispatcher: MultipoolDispatcher) => {
      const erc20Interface = new Interface(IErc20ABI) as Erc20Interface
      const dispatcherInterface = new Interface(MultipoolDispatcherAbi) as MultipoolDispatcherInterface

      const poolLength = await dispatcher.poolLength()
      const poolInfoCalls: Call[] = []
      for (let i = 0; i < poolLength.toNumber(); i++) {
        poolInfoCalls.push({
          target: dispatcher.address,
          callData: dispatcherInterface.encodeFunctionData('poolInfo', [i]),
          gasLimit: DEFAULT_GAS_LIMIT,
        })
      }

      const pools = (await multicall.callStatic.multicall(poolInfoCalls)).returnData
        .map((result, i) => {
          if (result.success) {
            const [multipool, multipoolToken, token0, token1] = dispatcherInterface.decodeFunctionResult(
              'poolInfo',
              result.returnData
            )
            return { multipool, multipoolToken, token0, token1 }
          } else {
            console.debug('dispatcher multipool list fetch errored', result)
          }
          return undefined
        })
        .filter((data) => !!data)

      const balanceCalls: Call[] = []
      //@ts-ignore
      for (const { multipoolToken } of pools) {
        balanceCalls.push({
          target: multipoolToken,
          callData: erc20Interface.encodeFunctionData('balanceOf', [account]),
          gasLimit: DEFAULT_GAS_LIMIT,
        })
      }

      const balances = (await multicall.callStatic.multicall(balanceCalls)).returnData.map((result, i) => {
        if (result.success) {
          const [balance] = erc20Interface.decodeFunctionResult('balanceOf', result.returnData)
          const poolInfo = pools[i]
          return { ...poolInfo, balance: BigNumber.from(balance) }
        } else {
          console.debug('multipool token balance fetch errored', result)
        }
        return undefined
      })

      const filtredBalances: IPoolInfo[] = []

      for (const data of balances) {
        if (data && !data.balance.isZero()) {
          //@ts-ignore
          filtredBalances.push(data)
        }
      }

      return filtredBalances
    },
    [account]
  )

  const fetchPositionInfo = useCallback(
    async (
      chainId: SupportedChainId,
      multipools: IPoolInfo[],
      multicall: UniswapInterfaceMulticall
    ): Promise<PositionInfo[]> => {
      const erc20Interface = new Interface(IErc20ABI) as Erc20Interface
      const multipoolInterface = new Interface(MultipoolAbi) as MultipoolInterface

      const totalSupplyCalls: Call[] = []
      for (const { multipoolToken } of multipools) {
        totalSupplyCalls.push({
          target: multipoolToken,
          callData: erc20Interface.encodeFunctionData('totalSupply'),
          gasLimit: DEFAULT_GAS_LIMIT,
        })
      }

      const totalSupplies = (await multicall.callStatic.multicall(totalSupplyCalls)).returnData.map((result, i) => {
        if (result.success) {
          const [totalSupply] = erc20Interface.decodeFunctionResult('totalSupply', result.returnData)
          const multipool = multipools[i]
          return { ...multipool, totalSupply: BigNumber.from(totalSupply) }
        } else {
          console.debug('multipool token totalSupply fetch errored', result)
        }
        return undefined
      })

      const revervesCall: Call[] = []
      for (const { multipool } of multipools) {
        revervesCall.push({
          target: multipool,
          callData: multipoolInterface.encodeFunctionData('getReserves'),
          gasLimit: DEFAULT_GAS_LIMIT,
        })
      }

      const reverves = (await multicall.callStatic.multicall(revervesCall)).returnData.map((result, i) => {
        if (result.success) {
          const [reserve0, reserve1] = multipoolInterface.decodeFunctionResult('getReserves', result.returnData)
          const multipool = totalSupplies[i]
          return { ...multipool, reserve0: BigNumber.from(reserve0), reserve1: BigNumber.from(reserve1) }
        } else {
          console.debug('multipool token totalSupply fetch errored', result)
        }
        return undefined
      })

      return await Promise.all(
        reverves.map(
          //@ts-ignore
          async ({ reserve0, reserve1, totalSupply, balance, multipool, multipoolToken, token0, token1 }) => {
            const tokens = await getTokens([token0, token1], chainId)

            const tokenA = tokens[token0] ?? new Token(chainId, token0, DEFAULT_ERC20_DECIMALS)
            const tokenB = tokens[token1] ?? new Token(chainId, token1, DEFAULT_ERC20_DECIMALS)

            const amountToken0 = CurrencyAmount.fromRawAmount(tokenA, reserve0.toString())
            const amountToken1 = CurrencyAmount.fromRawAmount(tokenB, reserve1.toString())

            // TO DO
            const multiPool = new Multipool(
              multipool,
              multipoolToken,
              [],
              totalSupply.toString(),
              amountToken0,
              amountToken1
            )
            const position = new MultipoolPosition({ multipool: multiPool, liquidity: balance.toString() })
            return {
              chainId,
              multipool: multiPool,
              position,
            }
          }
        )
      )
    },
    [account, getTokens]
  )

  const fetchPositionsForChain = useCallback(
    async (chainId: SupportedChainId): Promise<PositionInfo[]> => {
      try {
        const multicall = multicalls[chainId]
        const dispatcher = multipoolDispatchers[chainId]
        const multipoolInfos = await fetchMultipools(multicall, dispatcher)
        return await fetchPositionInfo(chainId, multipoolInfos, multicall)
      } catch (error) {
        console.error(`Failed to fetch strategy positions for chain ${chainId}`, error)
        return []
      }
    },
    [account, fetchMultipools, multicalls, multipoolDispatchers]
  )

  const fetchAllPositions = useCallback(async () => {
    positionsFetching.current = true
    const positions = (await Promise.all(chains.map(fetchPositionsForChain))).flat()
    positionsFetching.current = false
    setPositions(positions)
  }, [chains, fetchPositionsForChain, setPositions])

  // Fetches positions when existing positions are stale and the document has focus
  useEffect(() => {
    if (positionsFetching.current || cachedPositions?.stale === false) return
    else if (document.hasFocus()) {
      fetchAllPositions()
    } else {
      // Avoids refetching positions until the user returns to Interface to avoid polling unnused rpc data
      const onFocus = () => {
        fetchAllPositions()
        window.removeEventListener('focus', onFocus)
      }
      window.addEventListener('focus', onFocus)
      return () => {
        window.removeEventListener('focus', onFocus)
      }
    }
    return
  }, [fetchAllPositions, positionsFetching, cachedPositions?.stale])

  const positionsWithPrices: PositionInfo[] | undefined = useMemo(
    () =>
      positions?.map((position) => {
        const prices = [
          priceMap[currencyKey(position.multipool.token0)],
          priceMap[currencyKey(position.multipool.token1)],
        ]
        return { ...position, prices } as PositionInfo
      }),
    [positions, priceMap]
  )

  return { positions: positionsWithPrices, loading: pricesLoading || positionsLoading }
}
